export type NavArea =
  | "workflow"
  | "modules"
  | "admin"
  | "governance"
  | "operations"
  | "help"
  | "footer";

export type RegulatoryPhase = 1 | 2 | 3;

export interface NavigationRoute {
  id: string;
  path: string;
  matchPath?: string;
  /** When true, only the exact path (not nested children) counts as active. */
  matchExact?: boolean;
  label: string;
  title: string;
  icon: string;
  area: NavArea;
  roles?: string[];
  phase?: RegulatoryPhase;
  entitlementFeatureKey?: string;
  addonKey?: string;
  dashboardQuickAction?: boolean;
  discoverModule?: boolean;
  breadcrumbLabel?: string;
  translationKey?: string; // Key for i18n translation in navigation namespace
  translationNamespace?: string; // Namespace for translation (defaults to 'navigation')
}

export const NAVIGATION_ROUTES: NavigationRoute[] = [
  {
    id: "home",
    path: "/dashboard",
    label: "Dashboard",
    title: "Dashboard",
    icon: "bi-house-door",
    area: "workflow",
    dashboardQuickAction: false,
    breadcrumbLabel: "Dashboard",
    translationKey: "dashboard",
  },
  {
    id: "worklist",
    path: "/worklist",
    label: "Worklist",
    title: "Clinical Worklist",
    icon: "bi-clipboard2-pulse",
    area: "workflow",
    dashboardQuickAction: false,
    breadcrumbLabel: "Worklist",
    translationKey: "worklist",
  },
  {
    id: "patients",
    path: "/patients",
    label: "Patients",
    title: "Patients (Ctrl+P)",
    icon: "bi-people",
    area: "workflow",
    dashboardQuickAction: true,
    translationKey: "patients",
  },
  {
    id: "studies",
    path: "/studies",
    label: "Studies",
    title: "Studies",
    icon: "bi-journal-medical",
    area: "workflow",
    dashboardQuickAction: false,
    translationKey: "studies",
  },
  {
    id: "eeg-data-exchange",
    path: "/data/import-export",
    label: "EEG Data Exchange",
    title: "EEG Data Exchange / Conversion Workbench",
    icon: "bi-arrow-left-right",
    area: "workflow",
    roles: ["TECHNICIAN", "PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    dashboardQuickAction: false,
    breadcrumbLabel: "EEG Data Exchange",
    translationKey: "eegDataExchange",
  },
  {
    id: "reports",
    path: "/reports/review",
    matchPath: "/reports/review",
    matchExact: true,
    label: "Reports",
    title: "Reports",
    icon: "bi-file-earmark-text",
    area: "workflow",
    roles: ["PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    dashboardQuickAction: true,
    translationKey: "reports",
  },
  {
    id: "notifications",
    path: "/alerts",
    label: "Alerts",
    title: "Alerts",
    icon: "bi-bell",
    area: "workflow",
    dashboardQuickAction: false,
    translationKey: "alerts",
  },
  {
    id: "presets",
    path: "/presets/perspectives",
    label: "Presets",
    title: "Presets",
    icon: "bi-collection",
    area: "workflow",
    roles: ["PHYSICIAN", "TECHNICIAN", "NURSE", "ADMIN", "SUPER_ADMIN"],
    dashboardQuickAction: false,
    translationKey: "presets",
  },
  {
    id: "eeg-customization",
    path: "/eeg/customization/templates",
    matchPath: "/eeg/customization",
    label: "Review Configuration",
    title: "Review Configuration",
    icon: "bi-sliders2-vertical",
    area: "modules",
    roles: ["PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    discoverModule: true,
    translationKey: "eegConfiguration",
  },
  {
    id: "eeg-event-catalog",
    path: "/eeg/config/event-catalog",
    label: "Event Catalog",
    title: "Event Catalog",
    icon: "bi-tags",
    area: "modules",
    roles: ["PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    discoverModule: true,
    breadcrumbLabel: "Event Catalog",
    translationKey: "eventCatalog",
  },
  {
    id: "eeg-trend-templates",
    path: "/eeg/config/trend-templates",
    label: "Trend Templates",
    title: "Trend Templates",
    icon: "bi-graph-up",
    area: "modules",
    roles: ["PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    discoverModule: false,
    breadcrumbLabel: "Trend Templates",
    translationKey: "trendTemplates",
  },
  {
    id: "eeg-electrodes",
    path: "/eeg/customization/electrodes",
    label: "Recording & Channels",
    title: "Recording & Channels",
    icon: "bi-diagram-3",
    area: "modules",
    roles: ["PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    discoverModule: false,
    breadcrumbLabel: "Electrode Systems",
    translationKey: "eegElectrodes",
  },
  {
    id: "eeg-references",
    path: "/eeg/config/references",
    label: "EEG References",
    title: "EEG References",
    icon: "bi-bullseye",
    area: "modules",
    roles: ["PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    discoverModule: false,
    breadcrumbLabel: "EEG References",
    translationKey: "eegReferences",
  },
  {
    id: "eeg-non-eeg-signals",
    path: "/eeg/config/non-eeg-signals",
    label: "Non-EEG Signals",
    title: "Non-EEG Signals",
    icon: "bi-soundwave",
    area: "modules",
    roles: ["PHYSICIAN", "ADMIN", "SUPER_ADMIN"],
    discoverModule: false,
    breadcrumbLabel: "Non-EEG Signals",
    translationKey: "nonEegSignals",
  },
  {
    id: "signal-quality-monitoring",
    path: "/monitoring/signal-quality",
    label: "Signal Quality",
    title: "Signal Quality",
    icon: "bi-activity",
    area: "modules",
    phase: 2,
    entitlementFeatureKey: "SIGNAL_QUALITY_MONITORING",
    discoverModule: true,
    translationKey: "signalQuality",
  },
  {
    id: "statistical-analysis",
    path: "/reports/analytics",
    matchPath: "/reports/analytics",
    matchExact: true,
    label: "Analytics",
    title: "Analytics",
    icon: "bi-bar-chart-line",
    area: "modules",
    phase: 2,
    entitlementFeatureKey: "STATISTICAL_ANALYSIS",
    discoverModule: true,
    translationKey: "statisticalAnalysis",
  },
  {
    id: "real-time-collaboration",
    path: "/collaboration/sessions",
    label: "Review Sessions",
    title: "Review Sessions",
    icon: "bi-people",
    area: "modules",
    phase: 3,
    entitlementFeatureKey: "REAL_TIME_COLLABORATION",
    discoverModule: true,
    translationKey: "reviewSessions",
  },
  {
    id: "advanced-analytics",
    path: "/analytics/lab",
    label: "Analytics Lab",
    title: "Analytics Lab",
    icon: "bi-graph-up-arrow",
    area: "modules",
    phase: 3,
    entitlementFeatureKey: "ADVANCED_ANALYTICS",
    discoverModule: true,
    translationKey: "analyticsLab",
  },
  {
    id: "qa-consensus",
    path: "/qa/consensus",
    label: "QA Consensus",
    title: "QA Consensus",
    icon: "bi-check2-square",
    area: "operations",
    entitlementFeatureKey: "QA_DASHBOARD",
    dashboardQuickAction: false,
    discoverModule: true,
    translationKey: "qaConsensus",
  },
  {
    id: "annotation-dictionaries",
    path: "/admin/governance/annotations",
    label: "Annotations",
    title: "Annotations",
    icon: "bi-journal-text",
    area: "governance",
    roles: ["ADMIN", "SUPER_ADMIN"],
    discoverModule: true,
    translationKey: "annotationTypes",
  },
  {
    id: "document-templates",
    path: "/admin/governance/document-templates",
    label: "Report Templates",
    title: "Report Templates",
    icon: "bi-file-earmark-richtext",
    area: "governance",
    roles: ["ADMIN", "ORG_ADMIN", "SUPER_ADMIN"],
    entitlementFeatureKey: "document_templates_admin",
    discoverModule: true,
    translationKey: "documentTemplates",
  },
  {
    id: "shares",
    path: "/shares/inbox",
    matchPath: "/shares",
    label: "Shares",
    title: "Shares",
    icon: "bi-share",
    area: "modules",
    discoverModule: true,
    translationKey: "shares",
  },
  {
    id: "qa-dashboard",
    path: "/qa/dashboard",
    label: "QA Suite",
    title: "QA Suite",
    icon: "bi-clipboard2-data",
    area: "modules",
    entitlementFeatureKey: "QA_DASHBOARD",
    discoverModule: true,
    translationKey: "qaDashboard",
  },
  {
    id: "icu-workbench",
    path: "/monitoring/icu-workbench",
    label: "cEEG Monitoring",
    title: "cEEG Monitoring",
    icon: "bi-hospital",
    area: "modules",
    phase: 3,
    entitlementFeatureKey: "ICU_EMU_MONITORING",
    discoverModule: true,
    dashboardQuickAction: true,
    translationKey: "icuWorkbench",
  },
  {
    id: "icu-monitoring",
    path: "/monitoring/icu",
    matchPath: "/monitoring/icu",
    label: "ICU Monitoring",
    title: "ICU Monitoring",
    icon: "bi-heart-pulse",
    area: "modules",
    phase: 3,
    entitlementFeatureKey: "ICU_EMU_MONITORING",
    discoverModule: true,
    translationKey: "icuMonitoring",
  },
  {
    id: "tech-qc",
    path: "/monitoring/tech-qc",
    label: "Recording QC",
    title: "Recording QC",
    icon: "bi-clipboard-pulse",
    area: "operations",
    breadcrumbLabel: "Tech QC",
    translationKey: "techQc",
  },
  {
    id: "mobile-triage",
    path: "/monitoring/mobile-triage",
    label: "Specialty Monitoring",
    title: "Specialty Monitoring",
    icon: "bi-phone",
    area: "operations",
    translationKey: "mobileTriage",
  },
  {
    id: "ambulatory",
    path: "/monitoring/ambulatory",
    label: "Ambulatory",
    title: "Ambulatory Monitoring",
    icon: "bi-heart-pulse",
    area: "operations",
    phase: 2,
    entitlementFeatureKey: "AMBULATORY_EEG",
    discoverModule: true,
    translationKey: "ambulatory",
  },
  {
    id: "study-screening",
    path: "/monitoring/study-screening",
    label: "Study Screening",
    title: "Study Screening",
    icon: "bi-funnel",
    area: "operations",
    translationKey: "studyScreening",
  },
  {
    id: "neonatal",
    path: "/monitoring/neonatal",
    label: "Neonatal",
    title: "Neonatal Monitoring",
    icon: "bi-bandaid",
    area: "operations",
    phase: 3,
    entitlementFeatureKey: "NEONATAL_EEG",
    discoverModule: true,
    translationKey: "neonatal",
  },
  {
    id: "central-monitoring-wall",
    path: "/monitoring/wall",
    label: "Monitoring Wall",
    title: "Central Monitoring Wall",
    icon: "bi-grid-3x3-gap",
    area: "modules",
    phase: 3,
    entitlementFeatureKey: "ICU_EMU_MONITORING",
    discoverModule: true,
    translationKey: "centralMonitoringWall",
  },
  {
    id: "visualization-3d",
    path: "/visualization/3d",
    label: "3D Viewer",
    title: "3D Viewer",
    icon: "bi-badge-3d",
    area: "modules",
    phase: 3,
    entitlementFeatureKey: "3D_VISUALIZATION",
    discoverModule: true,
    translationKey: "threeDViewer",
  },
  {
    id: "source-localization",
    path: "/epilepsy/source-localization",
    label: "Source Localization",
    title: "Source Localization (ESI)",
    icon: "bi-radar",
    area: "modules",
    phase: 3,
    entitlementFeatureKey: "SOURCE_LOCALIZATION",
    discoverModule: true,
    roles: ["PHYSICIAN", "TECHNICIAN", "ADMIN", "SUPER_ADMIN"],
    breadcrumbLabel: "Source Localization",
    translationKey: "sourceLocalization",
  },
  {
    id: "imaging-co-review",
    path: "/imaging/co-review",
    label: "Imaging Co-Review",
    title: "EEG–PACS Imaging Co-Review",
    icon: "bi-images",
    area: "modules",
    roles: ["PHYSICIAN", "TECHNICIAN", "ADMIN", "SUPER_ADMIN"],
    breadcrumbLabel: "Co Review",
    translationKey: "imagingCoReview",
  },
  {
    id: "ai-service-ops",
    path: "/reports/ai-service-ops",
    matchPath: "/reports/ai-service-ops",
    matchExact: true,
    label: "AI Reports",
    title: "AI Reports",
    icon: "bi-clipboard2-check",
    area: "operations",
    roles: ["PHYSICIAN", "TECHNICIAN", "ADMIN", "SUPER_ADMIN"],
    breadcrumbLabel: "AI Reports",
    translationKey: "aiReportOps",
  },
  {
    id: "vendor-support",
    path: "/admin/vendor-support",
    label: "Vendor Support",
    title: "Vendor Support",
    icon: "bi-hdd-stack",
    area: "admin",
    roles: ["ADMIN", "SUPER_ADMIN"],
    breadcrumbLabel: "Vendor Support",
    translationKey: "vendorSupport",
  },
  {
    id: "telehealth",
    path: "/telehealth",
    label: "Telehealth",
    title: "Telehealth",
    icon: "bi-camera-video-fill",
    area: "modules",
    roles: [
      "PHYSICIAN",
      "TECHNICIAN",
      "NURSE",
      "ADMIN",
      "ORG_ADMIN",
      "SUPER_ADMIN",
    ],
    entitlementFeatureKey: "TELEHEALTH",
    addonKey: "telehealth",
    discoverModule: true,
    translationKey: "telehealth",
  },
  {
    id: "release-notes",
    path: "/help/release-notes",
    label: "Release Notes",
    title: "Release Notes",
    icon: "bi-journal-code",
    area: "help",
    discoverModule: true,
    translationKey: "releaseNotes",
  },
  {
    id: "account-profile",
    path: "/account/profile",
    label: "My Account",
    title: "My Account",
    icon: "bi-person-badge",
    area: "footer",
    translationKey: "myAccount",
  },
  {
    id: "settings",
    path: "/account/preferences/general",
    matchPath: "/account/preferences",
    label: "Preferences",
    title: "Preferences",
    icon: "bi-gear",
    area: "admin",
    translationKey: "preferences",
  },
  {
    id: "security",
    path: "/account/security/mfa",
    matchPath: "/account/security",
    label: "Security",
    title: "Security",
    icon: "bi-shield-lock",
    area: "admin",
    translationKey: "security",
  },
  {
    id: "account-sessions",
    path: "/account/sessions",
    label: "Sessions",
    title: "Sessions",
    icon: "bi-display",
    area: "footer",
    translationKey: "sessions",
  },
  {
    id: "users",
    path: "/admin/users/active",
    matchPath: "/admin/users",
    label: "People",
    title: "People",
    icon: "bi-people-fill",
    area: "admin",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "users",
  },
  {
    id: "admin-security",
    path: "/admin/security/access-control",
    matchPath: "/admin/security",
    label: "Security",
    title: "Security",
    icon: "bi-shield-lock",
    area: "admin",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "security",
  },
  {
    id: "organization",
    path: "/admin/organization/general",
    matchPath: "/admin/organization",
    label: "Organization",
    title: "Organization",
    icon: "bi-building",
    area: "admin",
    roles: ["ADMIN", "ORG_ADMIN", "SUPER_ADMIN"],
    translationKey: "organization",
  },
  {
    id: "ehr-connections",
    path: "/admin/interoperability/connections",
    matchPath: "/admin/interoperability/connections",
    label: "Interoperability",
    title: "Interoperability",
    icon: "bi-diagram-3",
    area: "admin",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "ehrConnections",
  },
  {
    id: "compliance",
    path: "/admin/governance/compliance",
    label: "Compliance",
    title: "Compliance",
    icon: "bi-shield-check",
    area: "governance",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "governance",
  },
  {
    id: "icd",
    path: "/admin/governance/icd-codes",
    label: "ICD-10 Codes",
    title: "ICD-10 Codes",
    icon: "bi-journal-medical",
    area: "governance",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "icdCodes",
  },
  {
    id: "priority-workflow",
    path: "/admin/governance/workflow-rules",
    matchPath: "/admin/governance/workflow-rules",
    label: "Governance",
    title: "Governance",
    icon: "bi-diagram-3",
    area: "governance",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "workflow",
  },
  {
    id: "notification-admin",
    path: "/admin/governance/alert-policies",
    label: "Alert Policies",
    title: "Alert Policies",
    icon: "bi-bell-fill",
    area: "governance",
    roles: ["ADMIN", "ORG_ADMIN", "SUPER_ADMIN"],
    translationKey: "alertPolicies",
  },
  {
    id: "appointments",
    path: "/appointments/upcoming",
    matchPath: "/appointments",
    label: "Appointments",
    title: "Appointments",
    icon: "bi-calendar-event",
    area: "operations",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "appointments",
  },
  {
    id: "ehr-sync-activity",
    path: "/admin/interoperability/sync-activity",
    label: "EHR Sync",
    title: "EHR Sync",
    icon: "bi-arrow-repeat",
    area: "operations",
    roles: ["ADMIN", "SUPER_ADMIN"],
    translationKey: "syncActivity",
  },
  {
    id: "billing",
    path: "/admin/billing/seats",
    matchPath: "/admin/billing",
    label: "Billing",
    title: "Billing",
    icon: "bi-credit-card",
    area: "operations",
    roles: ["SUPER_ADMIN", "ORG_ADMIN", "ADMIN"],
    translationKey: "billing",
  },
  {
    id: "help",
    path: "/help",
    // Nested help routes must not light up this leaf.
    matchExact: true,
    label: "Help",
    title: "Help",
    icon: "bi-question-circle",
    area: "help",
    translationKey: "help",
  },
  {
    id: "documentation",
    path: "/help/docs",
    label: "Documentation",
    title: "Documentation",
    icon: "bi-book",
    area: "help",
    translationKey: "documentation",
  },
  {
    id: "training",
    path: "/help/training",
    label: "Clinical Training Portal",
    title: "Clinical Training Portal",
    icon: "bi-mortarboard",
    area: "help",
    phase: 1,
    entitlementFeatureKey: "TRAINING",
    translationKey: "training",
  },
];

const ROLE_PRIORITY: Record<string, number> = {
  TECHNICIAN: 1,
  NURSE: 1,
  PHYSICIAN: 2,
  RESEARCHER: 2,
  ADMIN: 3,
  SUPER_ADMIN: 4,
};

function hasRequiredRole(
  roles: string[] | undefined,
  userRole?: string,
): boolean {
  if (!roles || roles.length === 0) return true;
  if (!userRole) return false;
  return roles.includes(userRole);
}

function hasRequiredPhase(
  phase: RegulatoryPhase | undefined,
  isPhaseEnabled?: (phase: RegulatoryPhase) => boolean,
): boolean {
  if (!phase || phase === 1) return true;
  return isPhaseEnabled ? isPhaseEnabled(phase) : false;
}

export function canAccessNavigationRoute(
  route: NavigationRoute,
  userRole?: string,
  isPhaseEnabled?: (phase: RegulatoryPhase) => boolean,
): boolean {
  return (
    hasRequiredRole(route.roles, userRole) &&
    hasRequiredPhase(route.phase, isPhaseEnabled)
  );
}

export function getNavigationByArea(
  area: NavArea,
  userRole?: string,
  isPhaseEnabled?: (phase: RegulatoryPhase) => boolean,
): NavigationRoute[] {
  return NAVIGATION_ROUTES.filter(
    (route) =>
      route.area === area &&
      canAccessNavigationRoute(route, userRole, isPhaseEnabled),
  );
}

export function getDashboardQuickActions(
  userRole?: string,
  isPhaseEnabled?: (phase: RegulatoryPhase) => boolean,
): NavigationRoute[] {
  return NAVIGATION_ROUTES.filter(
    (route) =>
      route.dashboardQuickAction &&
      canAccessNavigationRoute(route, userRole, isPhaseEnabled),
  );
}

export function getDiscoverModuleRoutes(
  userRole?: string,
  isPhaseEnabled?: (phase: RegulatoryPhase) => boolean,
): NavigationRoute[] {
  return NAVIGATION_ROUTES.filter(
    (route) =>
      route.discoverModule &&
      canAccessNavigationRoute(route, userRole, isPhaseEnabled),
  ).sort((a, b) => {
    const labelA = (a.label || a.title || "").toLowerCase();
    const labelB = (b.label || b.title || "").toLowerCase();
    return labelA.localeCompare(labelB);
  });
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

function splitPath(path: string): string[] {
  const normalized = normalizePath(path);
  return normalized === "/" ? [] : normalized.split("/").filter(Boolean);
}

function getMatchPath(route: NavigationRoute): string {
  return route.matchPath ?? route.path;
}

function segmentMatches(
  currentSegment: string | undefined,
  routeSegment: string,
) {
  return routeSegment.startsWith(":") || currentSegment === routeSegment;
}

function routeMatchesPath(
  currentPath: string,
  routePath: string,
  exact = false,
): boolean {
  const currentSegments = splitPath(currentPath);
  const routeSegments = splitPath(routePath);

  if (exact && currentSegments.length !== routeSegments.length) {
    return false;
  }

  if (!exact && routeSegments.length > currentSegments.length) {
    return false;
  }

  return routeSegments.every((routeSegment, index) =>
    segmentMatches(currentSegments[index], routeSegment),
  );
}

export function isActivePath(
  currentPath: string,
  routePath: string,
  exact = false,
): boolean {
  return routeMatchesPath(currentPath, routePath, exact);
}

export function isActiveNavRoute(
  currentPath: string,
  route: Pick<NavigationRoute, "path" | "matchPath" | "matchExact">,
): boolean {
  return isActivePath(
    currentPath,
    route.matchPath || route.path,
    Boolean(route.matchExact),
  );
}

function getBestRouteMatch(
  path: string,
  exact = false,
): NavigationRoute | undefined {
  const normalizedPath = normalizePath(path);
  const sorted = [...NAVIGATION_ROUTES].sort(
    (a, b) => getMatchPath(b).length - getMatchPath(a).length,
  );

  return sorted.find((route) =>
    routeMatchesPath(normalizedPath, getMatchPath(route), exact),
  );
}

export function getRouteByPath(path: string): NavigationRoute | null {
  return getBestRouteMatch(path, false) ?? null;
}

export function getExactRouteByPath(path: string): NavigationRoute | null {
  return getBestRouteMatch(path, true) ?? null;
}

export function getRouteLabelByPath(path: string): string | null {
  const match = getRouteByPath(path);
  return match?.breadcrumbLabel ?? match?.label ?? null;
}

export function getExactRouteLabelByPath(path: string): string | null {
  const match = getExactRouteByPath(path);
  return match?.breadcrumbLabel ?? match?.label ?? null;
}

export function getRouteTitleByPath(path: string): string | null {
  const match = getRouteByPath(path);
  return match?.title ?? match?.breadcrumbLabel ?? match?.label ?? null;
}

export function sortRolesByPriority(roles: string[]): string[] {
  return [...roles].sort(
    (a, b) => (ROLE_PRIORITY[b] ?? 0) - (ROLE_PRIORITY[a] ?? 0),
  );
}
